/**
 * Custom React Hooks for Hart Business Data Platform
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRecord, updateRecord, cleanFormData } from './hartUtils';
import { trackEvent } from '../../configureMixpanel';

/**
 * Custom hook for managing model detail pages (view/edit functionality)
 *
 * @param {Object} config - Configuration object
 * @param {string} config.apiEndpoint - API endpoint (e.g., '/hart/jobs')
 * @param {string} config.idParam - Record ID from URL params
 * @param {Object} config.initialData - Optional initial data from location.state
 * @param {Function} config.validateFn - Optional validation function
 * @param {Array} config.nullableFields - Optional array of fields to convert empty strings to null
 * @param {Function} config.onSuccess - Optional callback after successful save
 *
 * @returns {Object} State and handlers for detail page
 */
export function useModelDetails(config) {
  const {
    apiEndpoint,
    idParam,
    initialData,
    validateFn,
    nullableFields = [],
    onSuccess,
  } = config;

  const navigate = useNavigate();
  
  // State management
  const [data, setData] = useState(initialData || {});
  const [originalData, setOriginalData] = useState(null); // Store original data when entering edit mode
  const [editable, setEditable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Skip loading spinner if we have valid initial data (not empty object)
  const [loading, setLoading] = useState(!(initialData && Object.keys(initialData).length > 0));
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  // Tracks which record we've already fired hart_record_viewed for, so a
  // post-save refresh (loadData called again for the same idParam) doesn't
  // double-count, while navigating to a genuinely different record does.
  const trackedViewIdRef = useRef(null);

  // Load data function (can be called from anywhere)
  // @param {boolean} showSpinner - Whether to show loading spinner (default: true)
  const loadData = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setLoading(true);
      }
      setError(null);
      const fetchedData = await fetchRecord(apiEndpoint, idParam);
      setData(fetchedData);
      setOriginalData(null); // Reset original data when loading new record

      // Only track after the fetch actually succeeds - tracking on mount
      // regardless of outcome would count views the user never saw (e.g.
      // they landed on an error screen instead of the record).
      if (trackedViewIdRef.current !== idParam) {
        trackedViewIdRef.current = idParam;
        trackEvent('hart_record_viewed', {
          record_type: apiEndpoint.split('/').pop(),
          record_id: idParam,
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching record:', err);
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, idParam]);

  // Fetch data on mount or when ID changes
  useEffect(() => {
    // If we have initialData, refresh in background without showing spinner
    const hasInitialData = initialData && Object.keys(initialData).length > 0;
    loadData(!hasInitialData);
    // Note: initialData is from config and doesn't change, so not in deps
  }, [loadData]);

  // Submit handler for updates
  const handleSubmit = async () => {
    setSubmitting(true);
    setValidationErrors({});
    
    try {
      // Run validation if provided
      if (validateFn) {
        const errors = validateFn(data);
        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          setSubmitting(false);
          return;
        }
      }
      
      // Clean data - convert empty strings to null for nullable fields
      const cleanedData = cleanFormData(data, nullableFields);

      await updateRecord(apiEndpoint, idParam, cleanedData);
      
      // Re-fetch the data to get updated relationships (e.g., analysts with full details)
      // Pass false to skip loading spinner since user is already viewing content
      try {
        await loadData(false);
      } catch (refreshError) {
        console.warn('Failed to refresh data after save:', refreshError);
        // Non-critical: save succeeded, just refresh failed
      }
      
      setEditable(false);
      setOriginalData(null); // Clear original data after successful save
      trackEvent('hart_record_updated', {
        record_type: apiEndpoint.split('/').pop(),
        record_id: idParam,
        success: true,
      });
      if (onSuccess) onSuccess();
    } catch (error) {
      setError(error.message);
      console.error('Error updating record:', error);
      trackEvent('hart_record_updated', {
        record_type: apiEndpoint.split('/').pop(),
        record_id: idParam,
        success: false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle edit mode
  const handleEditToggle = () => {
    setEditable(prev => {
      if (prev) {
        // Canceling edit - restore original data
        if (originalData) {
          setData(originalData);
          setOriginalData(null);
        }
        setValidationErrors({}); // Clear validation errors when canceling
      } else {
        // Entering edit mode - save current data as original
        setOriginalData(JSON.parse(JSON.stringify(data))); 
      }
      return !prev;
    });
  };

  // Navigate back
  const handleBack = () => {
    navigate(-1);
  };

  // Cancel edit and revert changes
  const handleCancel = () => {
    setEditable(false);
  };

  return {
    // State
    data,
    setData,
    editable,
    setEditable,
    submitting,
    loading,
    error,
    validationErrors,
    
    // Handlers
    handleSubmit,
    handleEditToggle,
    handleBack,
    handleCancel,
  };
}

